import React from 'react';
import { Link } from 'react-router-dom';

function FAQ() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-indigo-800 to-blue-700 text-white py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-blue-100">Quick answers about usage, privacy, accuracy, and access.</p>
        </div>
      </div>

      <main className="container mx-auto px-4 py-12 max-w-5xl space-y-8">
        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-6">General</h2>
          <div className="space-y-5 text-gray-700">
            <div>
              <p className="font-semibold">Q: Is this service free?</p>
              <p>A: Yes, AdverseVideo Detector AI is currently free for research and educational use.</p>
            </div>
            <div>
              <p className="font-semibold">Q: Do I need to create an account?</p>
              <p>A: No account required. Simply paste a video URL and analyze.</p>
            </div>
            <div>
              <p className="font-semibold">Q: Which platforms do you support?</p>
              <p>A: Currently YouTube. TikTok, Twitter/X support coming soon.</p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-6">Privacy &amp; Security</h2>
          <div className="space-y-5 text-gray-700">
            <div>
              <p className="font-semibold">Q: Do you store the videos I analyze?</p>
              <p>A: No. Videos are downloaded temporarily for analysis and immediately deleted. We only store analysis results.</p>
            </div>
            <div>
              <p className="font-semibold">Q: What data do you collect?</p>
              <p>
                A: We collect: video URLs analyzed, analysis results, and anonymous usage statistics. See our{' '}
                <a href="/privacy.html" className="text-blue-600 hover:underline">Privacy Policy</a>.
              </p>
            </div>
            <div>
              <p className="font-semibold">Q: Is my analysis confidential?</p>
              <p>A: Yes. Analysis results are only visible to you.</p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-6">Technical</h2>
          <div className="space-y-5 text-gray-700">
            <div>
              <p className="font-semibold">Q: How accurate is the detection?</p>
              <p>A: Our model achieved 87.24% F1 score in research validation. However, we recommend human expert review for high-stakes decisions.</p>
            </div>
            <div>
              <p className="font-semibold">Q: How long does analysis take?</p>
              <p>A: 30-60 seconds per video, depending on length.</p>
            </div>
            <div>
              <p className="font-semibold">Q: What video lengths can you analyze?</p>
              <p>A: Currently optimized for videos under 10 minutes. Longer videos may take more time.</p>
            </div>
            <div>
              <p className="font-semibold">Q: Can I analyze private/unlisted videos?</p>
              <p>A: No, only public YouTube videos are supported.</p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-6">For Researchers</h2>
          <div className="space-y-5 text-gray-700">
            <div>
              <p className="font-semibold">Q: Can I use this for academic research?</p>
              <p>A: Yes! We encourage academic use. Contact us for bulk analysis access.</p>
            </div>
            <div>
              <p className="font-semibold">Q: Is the code open source?</p>
              <p>A: Selected components are open source. Full methodology published in our IEEE paper.</p>
            </div>
            <div>
              <p className="font-semibold">Q: Can I access the training dataset?</p>
              <p>A: Yes, for academic use. Contact research@adversevideo.org.</p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-6">For Institutions</h2>
          <div className="space-y-5 text-gray-700">
            <div>
              <p className="font-semibold">Q: Can multiple team members use this?</p>
              <p>A: Yes. Contact us for institutional access and team features.</p>
            </div>
            <div>
              <p className="font-semibold">Q: Do you offer API access?</p>
              <p>A: Currently in beta. Contact partnerships@adversevideo.org for early access.</p>
            </div>
            <div>
              <p className="font-semibold">Q: Can you help us integrate this into our workflow?</p>
              <p>A: Yes. We offer consultation for institutional deployments.</p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl shadow p-8">
          <h2 className="text-2xl font-bold mb-6">Limitations</h2>
          <div className="space-y-5 text-gray-700">
            <div>
              <p className="font-semibold">Q: What are the limitations?</p>
              <p>A:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Currently YouTube-only</li>
                <li>May produce false positives/negatives</li>
                <li>Not real-time (on-demand analysis)</li>
                <li>English language optimized</li>
                <li>Requires manual review for critical decisions</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold">Q: Is this a replacement for human analysts?</p>
              <p>A: No. This tool augments human analysis, not replaces it.</p>
            </div>
          </div>
        </section>

        <section className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center">
          <p className="text-gray-700 mb-4">Have more questions? Email: support@adversevideo.org</p>
          <div className="space-x-3">
            <Link
              to="/"
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
            >
              Back to Home
            </Link>
            <Link
              to="/how-it-works"
              className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
            >
              How It Works
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

export default FAQ;
